import React from "react";
import { Link } from "react-router-dom";
import { Heart, Plus } from "lucide-react";
import { useStore } from "../contexts/StoreContext";
import { useLang } from "../i18n";
import { formatPrice, stripHtml } from "../lib/api";
import { productImage } from "../assets/obi";

const PASTEL_BGS = [
  "bg-sky-100",
  "bg-pink-100",
  "bg-yellow-100",
  "bg-emerald-100",
  "bg-violet-100",
  "bg-orange-100",
];

const ProductCard = ({ product, index = 0 }) => {
  const { addToCart, toggleWishlist, inWishlist } = useStore();
  const { t } = useLang();
  const wished = inWishlist(product.id);
  const bg = PASTEL_BGS[index % PASTEL_BGS.length];
  const onSale = product.on_sale && product.regular_price && product.sale_price;
  const outOfStock = product.stock_status === "outofstock";

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="cartoon-card flex flex-col overflow-hidden group"
    >
      <Link to={`/product/${product.id}`} className="block relative">
        <div className={`relative ${bg} aspect-square border-b-4 border-slate-900 overflow-hidden flex items-center justify-center`}>
          <img
            src={productImage(product, index)}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
          {onSale && (
            <div className="absolute top-3 left-3 bg-pink-500 text-white font-display font-bold text-sm px-3 py-1 rounded-full border-2 border-slate-900 cartoon-shadow-sm">
              SALE
            </div>
          )}
          {outOfStock && (
            <div className="absolute top-3 left-3 bg-slate-200 text-slate-900 font-bold text-xs px-3 py-1 rounded-full border-2 border-slate-900">
              {t("product.out_of_stock")}
            </div>
          )}
          <button
            data-testid={`wishlist-btn-${product.id}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleWishlist(product.id);
            }}
            aria-label={wished ? t("product.remove_from_wishlist") : t("product.add_to_wishlist")}
            className="absolute top-3 right-3 w-10 h-10 bg-white border-4 border-slate-900 rounded-full flex items-center justify-center hover:bg-pink-100 transition-colors cartoon-shadow-sm"
          >
            <Heart
              className="w-4 h-4 text-pink-500"
              strokeWidth={2.5}
              fill={wished ? "#ec4899" : "none"}
            />
          </button>
        </div>
      </Link>

      <div className="flex flex-col flex-1 p-5">
        <Link to={`/product/${product.id}`}>
          <h3 className="font-display font-bold text-lg text-slate-900 leading-tight line-clamp-2 hover:text-sky-600 transition-colors">
            {product.name}
          </h3>
        </Link>
        {product.short_description && (
          <p className="text-sm text-slate-600 mt-2 line-clamp-2">
            {stripHtml(product.short_description)}
          </p>
        )}

        <div className="mt-auto pt-4 flex items-end justify-between gap-3">
          <div className="flex flex-col">
            {onSale ? (
              <>
                <span className="text-xs line-through text-slate-400">
                  {formatPrice(product.regular_price)}
                </span>
                <span className="font-display font-bold text-xl text-pink-600">
                  {formatPrice(product.sale_price)}
                </span>
              </>
            ) : (
              <span className="font-display font-bold text-xl text-slate-900">
                {formatPrice(product.price)}
              </span>
            )}
          </div>
          <button
            data-testid={`add-to-cart-${product.id}`}
            disabled={outOfStock}
            onClick={() => !outOfStock && addToCart(product)}
            className="cartoon-btn bg-yellow-300 hover:bg-yellow-200 disabled:opacity-50 disabled:cursor-not-allowed py-2 px-4 text-sm"
          >
            <Plus className="w-4 h-4" strokeWidth={3} />
            <span className="hidden sm:inline">{t("product.add_to_cart")}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
